// =====================================================
// HARDCORE LEVEL SYSTEM - Central Level Calculation
// =====================================================
// This file contains the ONLY level calculation logic.
// All components must use these functions for consistency.
// =====================================================

export interface LevelInfo {
  level: number;
  title: string;
  icon: string;
  color: string;
  glowColor: string;
  borderColor: string;
}

export interface LevelThreshold {
  level: number;
  xp: number;
  title: string;
  icon: string;
  color: string;
  glowColor: string;
  borderColor: string;
}

// HARDCORE XP Thresholds - Much harder to achieve!
// Level 10 requires ~500+ matches with many wins
export const LEVEL_THRESHOLDS: LevelThreshold[] = [
  { 
    level: 1, 
    xp: 0, 
    title: 'Rookie', 
    icon: '🆕',
    color: 'from-gray-500 via-gray-600 to-slate-600',
    glowColor: 'shadow-[0_0_10px_rgba(107,114,128,0.3)]',
    borderColor: 'border-gray-500/60'
  },
  { 
    level: 2, 
    xp: 50, 
    title: 'Beginner', 
    icon: '🎮',
    color: 'from-orange-500 via-amber-600 to-yellow-600',
    glowColor: 'shadow-[0_0_15px_rgba(249,115,22,0.3)]',
    borderColor: 'border-orange-500/60'
  },
  { 
    level: 3, 
    xp: 150, 
    title: 'Amateur', 
    icon: '🌟',
    color: 'from-slate-400 via-gray-500 to-zinc-500',
    glowColor: 'shadow-[0_0_15px_rgba(148,163,184,0.3)]',
    borderColor: 'border-slate-400/60'
  },
  { 
    level: 4, 
    xp: 350, 
    title: 'Intermediate', 
    icon: '🔥',
    color: 'from-indigo-400 via-purple-500 to-pink-500',
    glowColor: 'shadow-[0_0_20px_rgba(129,140,248,0.4)]',
    borderColor: 'border-indigo-400/60'
  },
  { 
    level: 5, 
    xp: 600, 
    title: 'Advanced', 
    icon: '⚡',
    color: 'from-blue-400 via-indigo-500 to-purple-500',
    glowColor: 'shadow-[0_0_25px_rgba(96,165,250,0.4)]',
    borderColor: 'border-blue-400/60'
  },
  { 
    level: 6, 
    xp: 1000, 
    title: 'Skilled', 
    icon: '🎯',
    color: 'from-green-400 via-emerald-500 to-teal-500',
    glowColor: 'shadow-[0_0_25px_rgba(74,222,128,0.4)]',
    borderColor: 'border-green-400/60'
  },
  { 
    level: 7, 
    xp: 1600, 
    title: 'Veteran', 
    icon: '🏆',
    color: 'from-emerald-400 via-teal-500 to-cyan-500',
    glowColor: 'shadow-[0_0_30px_rgba(52,211,153,0.5)]',
    borderColor: 'border-emerald-400/60'
  },
  { 
    level: 8, 
    xp: 2500, 
    title: 'Expert', 
    icon: '💎',
    color: 'from-cyan-400 via-blue-500 to-indigo-500',
    glowColor: 'shadow-[0_0_30px_rgba(34,211,238,0.5)]',
    borderColor: 'border-cyan-400/60'
  },
  { 
    level: 9, 
    xp: 4000, 
    title: 'Master', 
    icon: '⭐',
    color: 'from-yellow-400 via-amber-500 to-orange-500',
    glowColor: 'shadow-[0_0_30px_rgba(251,191,36,0.5)]',
    borderColor: 'border-yellow-400/60'
  },
  { 
    level: 10, 
    xp: 6000, 
    title: 'Legend', 
    icon: '👑',
    color: 'from-purple-500 via-pink-500 to-cyan-400',
    glowColor: 'shadow-[0_0_30px_rgba(168,85,247,0.5)]',
    borderColor: 'border-purple-400/60'
  },
];

// Stats interface for XP calculation
export interface PlayerStats {
  wins?: number;
  draws?: number;
  losses?: number;
  matches?: number;
  goals?: number;
  assists?: number;
  points?: number;
}

/**
 * Calculate XP from player stats using HARDCORE formula
 * 
 * XP Sources:
 * - Win: +15 XP
 * - Draw: +5 XP
 * - Match Played: +3 XP
 * - Goal Scored: +5 XP
 * - Assist: +3 XP
 * 
 * This makes Level 10 require ~500+ matches with many wins!
 */
export const calculateXP = (stats: PlayerStats | null | undefined): number => {
  if (!stats) return 0;
  
  const wins = stats.wins || 0;
  const draws = stats.draws || 0;
  const matches = stats.matches || 0;
  const goals = stats.goals || 0;
  const assists = stats.assists || 0;
  
  // Hardcore XP Formula
  const xp = (wins * 15) + (draws * 5) + (matches * 3) + (goals * 5) + (assists * 3);
  
  return xp;
};

/**
 * Get level info from XP value
 */
export const getLevelFromXP = (xp: number): LevelInfo => {
  // Find the highest level threshold that the XP meets
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_THRESHOLDS[i].xp) {
      return LEVEL_THRESHOLDS[i];
    }
  }
  return LEVEL_THRESHOLDS[0]; // Default to Rookie
};

/**
 * Get level info directly from player stats
 */
export const getLevelFromStats = (stats: PlayerStats | null | undefined): LevelInfo => {
  const xp = calculateXP(stats);
  return getLevelFromXP(xp);
};

/**
 * Get XP thresholds for progress bar calculation
 */
export const getLevelThresholds = (level: number): { current: number; next: number } => {
  const currentThreshold = LEVEL_THRESHOLDS.find(t => t.level === level);
  const nextThreshold = LEVEL_THRESHOLDS.find(t => t.level === level + 1);
  
  return {
    current: currentThreshold?.xp || 0,
    next: nextThreshold?.xp || (currentThreshold?.xp || 0) + 1000
  };
};

/**
 * Calculate progress percentage towards next level
 */
export const calculateProgress = (xp: number, level: number): number => {
  if (level >= 10) return 100; // Max level
  
  const { current, next } = getLevelThresholds(level);
  const progress = ((xp - current) / (next - current)) * 100;
  
  return Math.min(100, Math.max(0, progress));
};

/**
 * Get XP needed to reach next level
 */
export const getXPToNextLevel = (xp: number, level: number): number => {
  if (level >= 10) return 0; // Already max
  
  const { next } = getLevelThresholds(level);
  return Math.max(0, next - xp);
};

/**
 * Get a compact level badge text (e.g., "Lv.5")
 */
export const getLevelBadgeText = (level: number): string => {
  return `Lv.${level}`;
};
